import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useField } from 'react-final-form';
import { useAdminAccess } from 'contexts/adminAccess';

import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import FormHelperText from '@material-ui/core/FormHelperText';

import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';

interface StylesProps {
  value?: File | string | null;
}

const useStyles = makeStyles((theme) => ({
  dropzone: {
    height: 300,
    width: 300,
    boxSizing: 'border-box',
    border: (props: StylesProps) => (!!props.value ? 'none' : '1px dashed #6C13F9'),
    borderRadius: 16,
    position: 'relative',
    margin: 0,
    '&:hover': {
      cursor: 'pointer',
      border: '1px dashed #6C13F9',
      '& img': {
        filter: 'blur(8px) opacity(0.3)',
      },
      '& div': {
        opacity: 1,
      },
    },
  },
  description: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '18px',
    opacity: (props: StylesProps) => (props.value ? 0 : 1),
  },
  image: {
    width: '100%',
    height: '100%',
    maxWidth: 433,
    maxHeight: 433,
    borderRadius: 16,
    objectFit: 'contain',
  },
  secondaryText: {
    color: theme.palette.secondary.contrastText,
    fontSize: 12,
  },
  primaryText: {
    whiteSpace: 'nowrap',
  },
  errorText: {
    fontSize: 16,
  },
  descriptionText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

interface Props {
  name: string;
}

const FILE_SIZE_ERROR = 'file-too-large';
const FILE_TYPE_ERROR = 'file-invalid-type';

const ImageInputField = ({ name }: Props) => {
  const { canWrite } = useAdminAccess();
  const {
    input: { value, onChange },
    meta: { error, touched },
  } = useField(name);

  const classes = useStyles({ value });

  const [imageSrc, setImageSrc] = useState<string>('');

  useEffect(() => {
    if (typeof value === 'string') {
      setImageSrc(value);
      return;
    }

    if (value instanceof File) {
      const objectUrl = window.URL.createObjectURL(value);
      setImageSrc(objectUrl);

      return () => {
        window.URL.revokeObjectURL(objectUrl);
      };
    }

    setImageSrc('');
  }, [value]);

  const handleFileSelect = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    onChange(file);
  };

  const {
    getRootProps,
    getInputProps,
    open: openFilesSelection,
    fileRejections,
  } = useDropzone({
    disabled: !canWrite,
    noClick: true,
    noKeyboard: true,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    multiple: false,
    maxSize: 5242880,
    maxFiles: 1,
    onDropAccepted: handleFileSelect,
  });

  if (!canWrite) {
    return imageSrc ? (
      <img src={imageSrc} alt="product" className={classes.image} />
    ) : (
      <Typography>No image</Typography>
    );
  }

  return (
    <>
      <div {...getRootProps()} className={classes.dropzone} onClick={openFilesSelection}>
        <input {...getInputProps()} />
        {!!value && <img src={imageSrc} alt="product" className={classes.image} />}
        <div className={classes.description}>
          <UploadIcon />
          <div className={classes.descriptionText}>
            <Typography className={classes.primaryText}>
              Drag and drop files here or click
            </Typography>
            <Typography className={classes.secondaryText}>PNG, JPG, JPEG. Max size 5MB </Typography>
          </div>
        </div>
      </div>
      {!!(touched && error) && (
        <FormHelperText className={classes.errorText} error>
          {error}
        </FormHelperText>
      )}
      {fileRejections?.length > 1 && (
        <FormHelperText className={classes.errorText} error>
          You can only add one image
        </FormHelperText>
      )}
      {fileRejections.length === 1 &&
        fileRejections[0].errors?.map((error) => {
          if (error.code === FILE_SIZE_ERROR) {
            return (
              <FormHelperText
                key={`${error.code}-${error.message}`}
                className={classes.errorText}
                error>
                Image size must not exceed 5MB
              </FormHelperText>
            );
          }

          if (error.code === FILE_TYPE_ERROR) {
            return (
              <FormHelperText
                key={`${error.code}-${error.message}`}
                className={classes.errorText}
                error>
                Incorrect image file format
              </FormHelperText>
            );
          }

          return null;
        })}
    </>
  );
};

export default ImageInputField;
